/**
 * NOS-242: Generated SEO layer — schema.org JSON-LD generators.
 *
 * Pure data-in/JSON-out functions with zero framework or Supabase coupling so
 * the same generators can feed react-helmet-async today and Astro SSR heads at
 * M22 unchanged. Strategy is REGENERATE from NOS's own models (org identity,
 * posts, events, campaigns) rather than copying source-site structured data.
 *
 * Rules:
 * - Never emit empty/placeholder fields — invalid structured data is worse
 *   than none. Every optional field is dropped unless it has real content.
 * - Callers own privacy gating (e.g. EIN/taxID is only emitted when the
 *   caller explicitly passes it — see nonprofitOrganizationJsonLd docs).
 * - Serialization for <script type="application/ld+json"> must go through
 *   serializeJsonLd so user content (titles etc.) cannot break out of the
 *   script tag.
 */

export type JsonLdObject = Record<string, unknown>

/**
 * Safely serialize JSON-LD for embedding inside a <script> tag.
 * Escapes <, >, & as unicode sequences so user-supplied strings (e.g. a post
 * titled "</script><script>...") cannot terminate the script element. The
 * escaped output remains valid JSON and parses to identical data.
 */
export function serializeJsonLd(obj: JsonLdObject | JsonLdObject[]): string {
  return JSON.stringify(obj)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026")
}

/** Trimmed string or null when blank/absent. */
function clean(value: unknown): string | null {
  if (typeof value !== "string") return null
  const t = value.trim()
  return t.length > 0 ? t : null
}

/** Valid absolute http(s) URL or null. */
function httpUrl(value: unknown): string | null {
  const s = clean(value)
  if (!s) return null
  try {
    const u = new URL(s)
    if (u.protocol === "http:" || u.protocol === "https:") return s
  } catch {
    return null
  }
  return null
}

export interface NonprofitOrganizationJsonLdInput {
  name: string
  /** Canonical public site origin (custom domain or subdomain). */
  url: string
  logoUrl?: string | null
  /** Mission statement — becomes `description`. */
  missionStatement?: string | null
  legalName?: string | null
  /**
   * EIN — emitted as `taxID` ONLY when passed. Callers must gate this on an
   * explicit public-display opt-in; never pass it for sponsored/transferred
   * orgs where the stored EIN may belong to a different legal entity (M25).
   */
  ein?: string | null
  /** Free-text mailing address — schema.org `address` accepts Text. */
  address?: string | null
  foundingYear?: number | null
  /** Social profile URLs → `sameAs`. Invalid/non-http entries are dropped. */
  sameAs?: Array<string | null | undefined> | null
}

export function nonprofitOrganizationJsonLd(
  input: NonprofitOrganizationJsonLdInput
): JsonLdObject {
  const logo = httpUrl(input.logoUrl)
  const description = clean(input.missionStatement)
  const legalName = clean(input.legalName)
  const taxID = clean(input.ein)
  const address = clean(input.address)
  const sameAs = (input.sameAs ?? [])
    .map((u) => httpUrl(u))
    .filter((u): u is string => u !== null)
  const foundingYear =
    typeof input.foundingYear === "number" && Number.isInteger(input.foundingYear) && input.foundingYear > 0
      ? String(input.foundingYear)
      : null

  return {
    "@context": "https://schema.org",
    "@type": "NonprofitOrganization",
    name: input.name,
    url: input.url,
    ...(logo ? { logo } : {}),
    ...(description ? { description } : {}),
    ...(legalName ? { legalName } : {}),
    ...(taxID ? { taxID } : {}),
    ...(address ? { address } : {}),
    ...(foundingYear ? { foundingDate: foundingYear } : {}),
    ...(sameAs.length > 0 ? { sameAs } : {}),
  }
}

export interface BlogPostingJsonLdInput {
  title: string
  /** Canonical URL of the post. */
  url: string
  description?: string | null
  /** ISO date(-time) strings; omitted when absent — never emit empty dates. */
  datePublished?: string | null
  dateModified?: string | null
  imageUrl?: string | null
  orgName: string
  orgUrl: string
  orgLogoUrl?: string | null
}

export function blogPostingJsonLd(input: BlogPostingJsonLdInput): JsonLdObject {
  const description = clean(input.description)
  const datePublished = clean(input.datePublished)
  const dateModified = clean(input.dateModified) ?? datePublished
  const image = httpUrl(input.imageUrl)
  const publisherLogo = httpUrl(input.orgLogoUrl)

  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: input.title,
    url: input.url,
    mainEntityOfPage: { "@type": "WebPage", "@id": input.url },
    ...(description ? { description } : {}),
    ...(datePublished ? { datePublished } : {}),
    ...(dateModified ? { dateModified } : {}),
    ...(image ? { image: [image] } : {}),
    author: { "@type": "Organization", name: input.orgName, url: input.orgUrl },
    publisher: {
      "@type": "Organization",
      name: input.orgName,
      url: input.orgUrl,
      ...(publisherLogo
        ? { logo: { "@type": "ImageObject", url: publisherLogo } }
        : {}),
    },
  }
}

export interface EventOfferInput {
  name?: string | null
  /** Price in cents; null/undefined offers are dropped (no placeholder prices). */
  priceCents?: number | null
  /** Where the ticket can be bought — usually the event page itself. */
  url: string
}

export interface EventJsonLdInput {
  title: string
  /** Canonical URL of the event page. */
  url: string
  description?: string | null
  startDate: string
  endDate?: string | null
  imageUrl?: string | null
  /** "virtual" → VirtualLocation + Online attendance; else Place when known. */
  locationType?: string | null
  virtualUrl?: string | null
  locationName?: string | null
  locationAddress?: string | null
  orgName: string
  orgUrl: string
  offers?: EventOfferInput[] | null
}

export function eventJsonLd(input: EventJsonLdInput): JsonLdObject {
  const description = clean(input.description)
  const endDate = clean(input.endDate)
  const image = httpUrl(input.imageUrl)
  const virtualUrl = httpUrl(input.virtualUrl)
  const locationName = clean(input.locationName)
  const locationAddress = clean(input.locationAddress)

  const isVirtual = input.locationType === "virtual" && virtualUrl !== null
  let location: JsonLdObject | null = null
  if (isVirtual) {
    location = { "@type": "VirtualLocation", url: virtualUrl }
  } else if (locationName || locationAddress) {
    location = {
      "@type": "Place",
      ...(locationName ? { name: locationName } : {}),
      ...(locationAddress ? { address: locationAddress } : {}),
    }
  }

  const offers = (input.offers ?? [])
    .filter(
      (o): o is EventOfferInput & { priceCents: number } =>
        typeof o.priceCents === "number" && o.priceCents >= 0
    )
    .map((o) => {
      const offerName = clean(o.name)
      return {
        "@type": "Offer",
        ...(offerName ? { name: offerName } : {}),
        price: (o.priceCents / 100).toFixed(2),
        priceCurrency: "USD",
        url: o.url,
      }
    })

  return {
    "@context": "https://schema.org",
    "@type": "Event",
    name: input.title,
    url: input.url,
    startDate: input.startDate,
    ...(endDate ? { endDate } : {}),
    eventAttendanceMode: isVirtual
      ? "https://schema.org/OnlineEventAttendanceMode"
      : "https://schema.org/OfflineEventAttendanceMode",
    ...(location ? { location } : {}),
    ...(description ? { description } : {}),
    ...(image ? { image: [image] } : {}),
    organizer: { "@type": "Organization", name: input.orgName, url: input.orgUrl },
    ...(offers.length > 0 ? { offers } : {}),
  }
}

export interface DonateActionJsonLdInput {
  campaignTitle: string
  /** Canonical URL of the campaign/donate page (the action target). */
  campaignUrl: string
  description?: string | null
  orgName: string
  orgUrl: string
}

export function donateActionJsonLd(input: DonateActionJsonLdInput): JsonLdObject {
  const description = clean(input.description)
  return {
    "@context": "https://schema.org",
    "@type": "DonateAction",
    name: input.campaignTitle,
    ...(description ? { description } : {}),
    target: input.campaignUrl,
    recipient: {
      "@type": "NonprofitOrganization",
      name: input.orgName,
      url: input.orgUrl,
    },
  }
}
